import { FormControl, InputLabel, Select, MenuItem, Button } from "@mui/material";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';

import React from "react";


export const DATE_OPTIONS = [
  { label: "All", value: "" },
  { label: "1 Week", value: "7" },
  { label: "2 Weeks", value: "14" },
  { label: "4 Weeks", value: "28" },
  { label: "1 Quarter", value: "90" },
  { label: "Custom", value: "custom" },
];

export const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "In-Progress", value: "in-progress" },
  { label: "Completed", value: "completed" },
];

export default function CustomFilterBar({
  dateRange,
  setDateRange,
  customStart,
  setCustomStart,
  customEnd,
  setCustomEnd,
  status,
  setStatus,
  DATE_OPTIONS = [],
  STATUS_OPTIONS = [],
  style = {},
  dateLabel = "Date Range",
}) {
  // Local state for date pickers
  const [startOpen, setStartOpen] = React.useState(false);
  const [endOpen, setEndOpen] = React.useState(false);
  // Initialize from props if available
  const [pendingStart, setPendingStart] = React.useState(customStart || null);
  const [pendingEnd, setPendingEnd] = React.useState(customEnd || null);

  // Update local state when props change (in case they are reset externally)
  React.useEffect(() => {
    setPendingStart(customStart || null);
    setPendingEnd(customEnd || null);
  }, [customStart, customEnd]);

  const showDateError = dateRange === "custom" && pendingStart && pendingEnd && new Date(pendingEnd) < new Date(pendingStart);
  const canApply = dateRange === "custom" && pendingStart && pendingEnd && !showDateError;

  // Helper to format date to YYYY-MM-DD or empty string (without timezone conversion)
  const toAPIDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <div style={ { display: "flex", alignItems: "center", gap: 8, overflow: "visible", ...style, flexDirection: 'row', flexWrap: 'wrap', position: 'relative' } }>

      {/* Date Range Filter */ }
      <FormControl size="small" variant="outlined" sx={ { minWidth: 160, height: 40 } }>
        <InputLabel
          id="date-range-label"
          shrink={ true }
          sx={ { fontSize: 14, fontWeight: 500, backgroundColor: 'white', px: 0.5 } }
        >
          { dateLabel }
        </InputLabel>
        <Select
          labelId="date-range-label"
          id="date-range-select"
          value={ typeof dateRange === 'object' ? 'custom' : dateRange }
          displayEmpty
          notched={ true }
          onChange={ (e) => setDateRange(e.target.value) }
          label={ dateLabel }
          sx={ {
            width: 160,
            height: 40,
            fontSize: 14,
            fontWeight: 500,
            borderRadius: 2,
            background: '#fff',
            '& .MuiSelect-select': {
              padding: '10px 14px',
              minHeight: 'unset',
              display: 'flex',
              alignItems: 'center',
            },
          } }
        >
          { DATE_OPTIONS.map((opt) => (
            <MenuItem key={ opt.value } value={ opt.value } sx={ { fontSize: 14 } }>{ opt.label }</MenuItem>
          )) }
        </Select>
      </FormControl>

      <LocalizationProvider dateAdapter={ AdapterDateFns }>
        { (dateRange === "custom" || typeof dateRange === 'object') && (
          <Box sx={ { display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' } }>
            <DatePicker
              label="Start Date"
              value={ pendingStart }
              onChange={ (newValue) => setPendingStart(newValue) }
              open={ startOpen }
              onOpen={ () => setStartOpen(true) }
              onClose={ () => setStartOpen(false) }
              renderInput={ (params) => (
                <TextField
                  { ...params }
                  size="small"
                  sx={ {
                    width: 148,
                    '& .MuiInputBase-root': {
                      height: 40,
                      fontSize: 13,
                      borderRadius: 2,
                      background: '#fff',
                    },
                    '& .MuiOutlinedInput-input': { padding: '10px 10px' },
                  } }
                  onClick={ () => setStartOpen(true) }
                  InputLabelProps={ { shrink: true, sx: { backgroundColor: 'white', px: 0.5, fontSize: 13 } } }
                />
              ) }
              maxDate={ pendingEnd || undefined }
            />
            <DatePicker
              label="End Date"
              value={ pendingEnd }
              onChange={ (newValue) => setPendingEnd(newValue) }
              open={ endOpen }
              onOpen={ () => setEndOpen(true) }
              onClose={ () => setEndOpen(false) }
              renderInput={ (params) => (
                <TextField
                  { ...params }
                  size="small"
                  sx={ {
                    width: 148,
                    '& .MuiInputBase-root': {
                      height: 40,
                      fontSize: 13,
                      borderRadius: 2,
                      background: '#fff',
                      borderColor: showDateError ? '#f44336' : undefined,
                    },
                    '& .MuiOutlinedInput-input': { padding: '10px 10px' },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: showDateError ? '#f44336 !important' : undefined,
                    },
                  } }
                  onClick={ () => setEndOpen(true) }
                  InputLabelProps={ { shrink: true, sx: { backgroundColor: 'white', px: 0.5, fontSize: 13 } } }
                />
              ) }
              minDate={ pendingStart || undefined }
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={ !canApply }
              onClick={ () => {
                if (canApply) {
                  setCustomStart(pendingStart);
                  setCustomEnd(pendingEnd);
                  setDateRange({
                    startDate: toAPIDate(pendingStart),
                    endDate: toAPIDate(pendingEnd)
                  });
                  setEndOpen(false);
                }
              } }
              sx={ {
                height: 40,
                minWidth: 72,
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 2,
                textTransform: 'none',
                flexShrink: 0,
              } }
            >Apply</Button>
            { showDateError && (
              <div style={ { color: '#f44336', fontSize: 12, whiteSpace: 'nowrap' } }>
                End date before start date
              </div>
            ) }
          </Box>
        ) }
      </LocalizationProvider>

      {/* Status Filter */ }
      { Array.isArray(STATUS_OPTIONS) && STATUS_OPTIONS.length > 0 && (
        <FormControl size="small" variant="outlined" style={ { minWidth: 120 } }>
          <InputLabel id="status-label">Status</InputLabel>
          <Select
            labelId="status-label"
            id="status-select"
            value={ status }
            onChange={ (e) => setStatus(e.target.value) }
            label="Status"
          >
            { STATUS_OPTIONS.map((opt) => (
              <MenuItem key={ opt.value } value={ opt.value }>{ opt.label }</MenuItem>
            )) }
          </Select>
        </FormControl>
      ) }

      {/* Clear Filters Button */ }
      <Button
        variant="outlined"
        size="small"
        style={ { height: 40, fontWeight: 600, fontSize: 13, letterSpacing: 0.3, flexShrink: 0 } }
        onClick={ () => {
          setDateRange("");
          setCustomStart("");
          setCustomEnd("");
          setPendingStart(null);
          setPendingEnd(null);
          if (setStatus) setStatus("");
        } }
      >
        Clear Filters
      </Button>
    </div>
  );
}